import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../api/auth';
import { switchLanguage } from '../i18n';
import { useAuthStore } from '../store/auth';
import { ModalSheet } from '../components/ui/ModalSheet';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedPanel } from '../components/ui/ThemedSurface';

interface SettingRow {
  label: string;
  value?: string;
  to?: string;
  danger?: boolean;
  onPress?: () => void;
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
    </svg>
  );
}

function SettingSection({ title, rows, onNavigate }: { title: string; rows: SettingRow[]; onNavigate: (to: string) => void }) {
  return (
    <ThemedPanel className="p-0">
      <div className="relative">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold}66 50%, transparent 100%)` }} />
        <div className="px-4 pt-4 text-sm font-semibold" style={{ color: C.text }}>{title}</div>
        <div className="mt-2">
          {rows.map((row, index) => (
            <button
              key={row.label}
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition active:scale-[0.99]"
              style={{ borderTop: index > 0 ? `1px solid ${C.line}` : undefined }}
              onClick={() => {
                if (row.onPress) row.onPress();
                else if (row.to) onNavigate(row.to);
              }}
            >
              <span className="min-w-0 truncate text-sm font-medium" style={{ color: row.danger ? C.sell : C.text }}>{row.label}</span>
              <span className="flex shrink-0 items-center gap-2 text-xs" style={{ color: row.danger ? C.sell : C.muted }}>
                {row.value ? <span className="max-w-[120px] truncate">{row.value}</span> : null}
                {!row.danger ? <ChevronRightIcon /> : null}
              </span>
            </button>
          ))}
        </div>
      </div>
    </ThemedPanel>
  );
}

function SettingHero() {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }} />
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xl font-semibold leading-none" style={{ color: C.text }}>{t('setting.title')}</div>
            <div className="mt-2 text-xs leading-relaxed" style={{ color: C.muted }}>{t('setting.subtitle')}</div>
          </div>
          <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl border" style={{ borderColor: `${C.gold}66`, background: C.chipBg, color: C.gold, boxShadow: `0 0 20px ${C.gold}18` }}>
            <svg viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .92V20.5a2 2 0 0 1-4 0v-.18A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.92-1H3.5a2 2 0 0 1 0-4h.18A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.92V3.5a2 2 0 0 1 4 0v.18a1.7 1.7 0 0 0 1 .92 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.18.37.5.7.92 1h.18a2 2 0 0 1 0 4h-.18a1.7 1.7 0 0 0-.92 1Z" />
            </svg>
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

function LanguageModal({ open, currentLanguage, onClose, onSelect }: { open: boolean; currentLanguage: string; onClose: () => void; onSelect: (language: 'zh' | 'en') => void }) {
  const { t } = useTranslation();
  const options: { code: 'zh' | 'en'; label: string }[] = [
    { code: 'zh', label: t('language.zh') },
    { code: 'en', label: t('language.en') },
  ];

  return (
    <ModalSheet open={open} onClose={onClose} title={t('setting.chooseLanguage')}>
      <div className="flex flex-col gap-2">
        {options.map((option) => {
          const active = currentLanguage.toLowerCase().startsWith(option.code);

          return (
            <button
              key={option.code}
              type="button"
              onClick={() => onSelect(option.code)}
              className="flex h-12 items-center justify-between rounded-xl border px-4 text-left text-sm font-semibold transition active:scale-[0.99]"
              style={{
                borderColor: active ? `${C.gold}66` : C.softBorder,
                background: active ? C.chipBg : C.inputBg,
                color: active ? C.gold : C.text,
                boxShadow: active ? `0 0 16px ${C.gold}14` : undefined,
              }}
            >
              <span>{option.label}</span>
              {active ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                </svg>
              ) : null}
            </button>
          );
        })}
      </div>
    </ModalSheet>
  );
}

export function SettingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const languageLabel = i18n.language.startsWith('zh') ? t('language.zh') : t('language.en');

  async function handleLogout() {
    try {
      await authAPI.logout({ refresh_token: refreshToken ?? undefined });
    } catch {
      // Ignore logout API failures; local auth state still needs clearing.
    }
    clear();
    navigate('/login', { replace: true });
  }

  const accountRows: SettingRow[] = [
    { label: t('setting.changePwd'), to: '/change-pwd' },
    { label: t('setting.secureSettings'), to: '/secure' },
    { label: t('setting.bindWallet'), to: '/bind-wallet' },
  ];

  const preferenceRows: SettingRow[] = [
    { label: t('setting.language'), value: languageLabel, onPress: () => setLanguageModalOpen(true) },
    { label: t('setting.about'), to: '/terms' },
  ];

  const systemRows: SettingRow[] = [
    { label: t('setting.logout'), danger: true, onPress: () => void handleLogout() },
  ];

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('setting.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: '20px' }}>
          <SettingHero />
          <SettingSection title={t('setting.accountSecurity')} rows={accountRows} onNavigate={navigate} />
          <SettingSection title={t('setting.preferences')} rows={preferenceRows} onNavigate={navigate} />
          <SettingSection title={t('setting.system')} rows={systemRows} onNavigate={navigate} />
        </div>
        <LanguageModal
          open={languageModalOpen}
          currentLanguage={i18n.language}
          onClose={() => setLanguageModalOpen(false)}
          onSelect={(language) => {
            switchLanguage(language);
            setLanguageModalOpen(false);
          }}
        />
      </div>
    </div>
  );
}

export default SettingPage;
